/**
 * @fileoverview WhenToVisit.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { WhenToTravelData } from '../assets/Assets'
import SEO from '../components/ui/SEO'
import { apiFetch } from '../utils/api'
import ExperienceCard from '../components/experiences/ExperienceCard'
import ExperienceModal from '../components/experiences/ExperienceModal'

const experienceSlugMap = {
    "whale-watching": "Mirissa Whale Watching",
    "polonnaruwa-ancient-city": "Ruins of Polonnaruwa",
    "ella-odyssey": "The Scenic Ella Train Journey",
    "tea-factory-tour": "Ceylon Tea Factory & Plantation",
    "surfing-weligama": "Surfing at Weligama Bay",
    "turtle-conservation": "Sea Turtle Hatchery & Conservation",
    "elephant-gathering": "The Great Elephant Gathering",
    "cultural-dance": "Kandyan Cultural Performance",
    "cooking-class": "Authentic Sri Lankan Cooking",
    "sigiriya-climb": "Sigiriya Rock Fortress",
    "galle-fort-sunset": "Sunset Walk on Galle Fort"
};

const getExpenseColor = (rateStr) => {
    const rate = rateStr.split(' ')[0].length; // e.g., "$$$ (Peak)" -> "$$$" -> 3
    if (rate <= 1) return 'text-emerald-500'
    if (rate <= 2) return 'text-lime-500'
    if (rate <= 3) return 'text-yellow-500'
    if (rate <= 4) return 'text-orange-500'
    return 'text-red-500'
}

const getExpenseText = (rateStr) => {
    if (rateStr.includes("Peak")) return 'Premium / Peak'
    if (rateStr.includes("Shoulder")) return 'Moderate'
    return 'Budget-Friendly'
}

const getWeatherIconClass = (text) => {
    if (!text) return 'fi-br-cloud';
    const t = text.toLowerCase();
    
    // Specific mappings for the Sri Lankan weather conditions
    if (t.includes('sunny and clear')) return 'fi-br-sun text-yellow-500';
    if (t.includes('thunderstorm')) return 'fi-br-bolt text-blue-600';
    if (t.includes('continuous heavy rain')) return 'fi-br-cloud-showers-heavy text-blue-600';
    if (t.includes('light rain')) return 'fi-br-cloud-drizzle text-blue-400';
    if (t.includes('overcast and cloudy')) return 'fi-br-cloud text-gray-500';
    if (t.includes('windy')) return 'fi-br-wind text-gray-400';
    
    // Fallbacks just in case
    if (t.includes('sun') || t.includes('clear')) return 'fi-br-sun text-yellow-500';
    if (t.includes('partly')) return 'fi-br-cloud-sun text-yellow-400';
    if (t.includes('rain') || t.includes('shower') || t.includes('drizzle')) return 'fi-br-cloud-rain text-blue-500';
    if (t.includes('heavy rain') || t.includes('storm') || t.includes('thunder')) return 'fi-br-cloud-showers-heavy text-blue-600';
    if (t.includes('wind') || t.includes('breeze')) return 'fi-br-wind text-gray-400';
    if (t.includes('snow')) return 'fi-br-snowflake text-sky-400';
    
    return 'fi-br-cloud text-gray-500';
}

function WhenToVisit() {
    const [searchParams, setSearchParams] = useSearchParams();
    const month = searchParams.get('month') || new Date().toLocaleString('default', { month: 'long' });
    
    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const buttonRefs = useRef([]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
        }
    };

    const [selectedData, setSelectedData] = useState(() => {
        const found = WhenToTravelData.find(d => d.id === month?.toLowerCase());
        return found || WhenToTravelData[new Date().getMonth()];
    });

    const [dbMonths, setDbMonths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedExperience, setSelectedExperience] = useState(null);

    useEffect(() => {
        setLoading(true);
        apiFetch('/when-to-travel')
            .then(res => {
                if (Array.isArray(res)) {
                    setDbMonths(res);
                } else {
                    setDbMonths([]);
                }
            })
            .catch(() => {
                setDbMonths([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (month) {
            const found = WhenToTravelData.find(d => d.id === month.toLowerCase());
            if (found) {
                setSelectedData(found);
                
                // Scroll to the selected month button
                const index = WhenToTravelData.findIndex(d => d.id === month.toLowerCase());
                if (buttonRefs.current[index] && scrollRef.current) {
                    const button = buttonRefs.current[index];
                    const container = scrollRef.current;
                    const scrollLeft = button.offsetLeft - (container.clientWidth / 2) + (button.clientWidth / 2);
                    container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                }
            }
        }
        window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    }, [month]);

    if (loading) {
        return (
            <div className='flex w-full min-h-screen justify-center items-center bg-gray-50'>
                <div className='w-12 h-12 border-4 border-KITH-primary border-t-transparent rounded-full animate-spin'></div>
            </div>
        );
    }

    const currentDbMonth = dbMonths.find(m => m.month.toLowerCase() === selectedData.month.substring(0, 3).toLowerCase()) || {};
    
    // We expect the backend to populate 'dominantExperience' and 'experiences'
    const featuredExperience = currentDbMonth.dominantExperience 
        ? {
            id: currentDbMonth.dominantExperience._id,
            name: currentDbMonth.dominantExperience.title,
            description: currentDbMonth.dominantExperience.description,
            image: (currentDbMonth.dominantExperience.image && typeof currentDbMonth.dominantExperience.image === 'object' && (currentDbMonth.dominantExperience.image.l || currentDbMonth.dominantExperience.image.h))
                ? { l: currentDbMonth.dominantExperience.image.l || currentDbMonth.dominantExperience.image.h, h: currentDbMonth.dominantExperience.image.h || currentDbMonth.dominantExperience.image.l }
                : { l: currentDbMonth.dominantExperience.image || '', h: currentDbMonth.dominantExperience.image || '' },
            categories: currentDbMonth.dominantExperience.categories || []
        } 
        : null;

    const otherExperiences = (currentDbMonth.experiences || []).map(exp => ({
        id: exp._id,
        name: exp.title,
        description: exp.description,
        image: (exp.image && typeof exp.image === 'object' && (exp.image.l || exp.image.h))
            ? { l: exp.image.l || exp.image.h, h: exp.image.h || exp.image.l }
            : { l: exp.image || '', h: exp.image || '' },
        categories: exp.categories || []
    }));

    const visitSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": `Why should I visit Sri Lanka in ${selectedData.month}?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": selectedData.description
                }
            },
            {
                "@type": "Question",
                "name": `What is the weather like in Sri Lanka in ${selectedData.month}?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `The weather is typically ${selectedData.weather}.`
                }
            },
            {
                "@type": "Question",
                "name": `What is the expense rate in Sri Lanka in ${selectedData.month}?`,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": `The expense rate is categorized as ${selectedData.expenseRate}.`
                }
            }
        ]
    };

    const numericExpense = selectedData.expenseRate.split(' ')[0].length;

    return (
        <div className='w-full min-h-screen bg-gray-50 flex flex-col'>
            <SEO
                title={`Visit Sri Lanka in ${selectedData.month}`}
                description={selectedData.description}
                keywords={`best time to visit Sri Lanka, Sri Lanka in ${selectedData.month}, ${selectedData.month} weather Sri Lanka, ${selectedData.month} travel guide Sri Lanka, ${selectedData.weather}`}
                schema={visitSchema}
            />

            {/* Hero Banner - fixed height, no full-screen overflow */}
            <AnimatePresence mode="wait">
                <motion.div 
                    key={`hero-${selectedData.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className='w-full px-[5%] md:px-[10%] pt-28 md:pt-32'
                >
                    <div className='w-full h-52 md:h-72 rounded-3xl overflow-hidden shadow-md relative'>
                        <img
                            src={featuredExperience?.image?.h || selectedData.image.h}
                            alt={`Sri Lanka in ${selectedData.month}`}
                            className='w-full h-full object-cover'
                        />
                        <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent'></div>
                        <div className='absolute bottom-6 left-8'>
                            <span className='text-white/70 text-xs font-bold uppercase tracking-widest'>Visiting in</span>
                            <h1 className='text-3xl md:text-4xl font-black text-white'>{selectedData.month}</h1>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            <div className='w-full px-[5%] md:px-[10%] py-10 flex flex-col gap-8'>

                {/* Month Navigator */}
                <div className='w-full flex flex-col gap-3 items-center bg-white p-4 rounded-2xl shadow-sm border border-gray-100'>
                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Browse by month</span>
                    <div className='relative w-full flex items-center group max-w-4xl'>
                        {/* Left Arrow */}
                        <button
                            onClick={() => scroll('left')}
                            className='absolute -left-12 lg:-left-16 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-gray-900 rounded-full shadow-md border border-gray-800 hidden md:flex items-center justify-center text-white hover:bg-KITH-primary hover:border-KITH-primary transition-colors'
                        >
                            <i className='fi fi-br-angle-left mt-1 -ml-0.5'></i>
                        </button>

                        {/* Edge Gradients */}
                        <div className='absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none z-10 hidden md:block'></div>
                        <div className='absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 hidden md:block'></div>

                        <div ref={scrollRef} className='flex overflow-x-auto gap-2 scrollbarHide w-full justify-start px-2 relative'>
                            {WhenToTravelData.map((data, index) => (
                                <button
                                    key={data.id}
                                    ref={el => buttonRefs.current[index] = el}
                                    onClick={() => setSearchParams({ month: data.id })}
                                    className={`px-4 py-2 rounded-full whitespace-nowrap font-bold text-xs transition-all border shrink-0 ${data.id === selectedData.id
                                        ? 'bg-KITH-primary text-white border-KITH-primary shadow-sm'
                                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-KITH-primary hover:text-KITH-primary'
                                        }`}
                                >
                                    {data.month}
                                </button>
                            ))}
                        </div>

                        {/* Right Arrow */}
                        <button
                            onClick={() => scroll('right')}
                            className='absolute -right-12 lg:-right-16 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-gray-900 rounded-full shadow-md border border-gray-800 hidden md:flex items-center justify-center text-white hover:bg-KITH-primary hover:border-KITH-primary transition-colors'
                        >
                            <i className='fi fi-br-angle-right mt-1 -mr-0.5'></i>
                        </button>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div 
                        key={`content-${selectedData.id}`}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className='flex flex-col gap-8'
                    >
                        {/* Info Row - description + weather + expense side by side */}
                <div className='grid grid-cols-1 lg:grid-cols-3 gap-5'>
                    {/* Description */}
                    <div className='lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-3'>
                        <h2 className='text-lg font-bold text-gray-800'>Why visit in {selectedData.month}?</h2>
                        <div 
                            className='text-gray-500 text-base leading-relaxed text-justify prose prose-sm max-w-none' 
                            dangerouslySetInnerHTML={{ __html: currentDbMonth.description || selectedData.description }} 
                        />
                    </div>

                    {/* Weather + Expense stacked */}
                    <div className='flex flex-col gap-4'>
                        <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4'>
                            <div className={`w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-3xl shrink-0 border border-slate-100`}>
                                <i className={getWeatherIconClass(selectedData.weather)}></i>
                            </div>
                            <div className='flex flex-col'>
                                <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Weather & Temp</span>
                                <span className='text-sm font-bold text-gray-800'>{selectedData.weather}</span>
                                <span className='text-xs font-bold text-gray-500 mt-0.5'>{selectedData.temperature}</span>
                            </div>
                        </div>

                        <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4'>
                            <div className='flex flex-col flex-grow'>
                                <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Expense Rate</span>
                                <span className={`text-sm font-bold ${getExpenseColor(selectedData.expenseRate)}`}>
                                    {getExpenseText(selectedData.expenseRate)}
                                </span>
                                <div className='flex gap-1 mt-1'>
                                    {[...Array(5)].map((_, i) => (
                                        <span 
                                            key={i} 
                                            className={`text-lg font-bold ${i < numericExpense ? getExpenseColor(selectedData.expenseRate) : 'text-gray-200'}`}
                                        >
                                            $
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Featured Experience */}
                {featuredExperience && (
                    <div className='flex flex-col gap-4'>
                        <div className='flex items-center gap-3'>
                            <h2 className='text-base font-bold text-gray-700 uppercase tracking-wide'>Highlight of {selectedData.month}</h2>
                        </div>
                        
                        <div className='w-full sm:w-80 md:w-96 bg-white rounded-3xl border border-gray-100 shadow-sm'>
                            <ExperienceCard 
                                experience={featuredExperience} 
                                onClick={setSelectedExperience}
                                variant="overlay"
                            />
                        </div>
                    </div>
                )}

                {/* More Things to Do */}
                <div className='flex flex-col gap-5'>
                    <div className='flex items-center gap-3'>
                        <h2 className='text-base font-bold text-gray-700 uppercase tracking-wide'>More Things to Do in {selectedData.month}</h2>
                    </div>

                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
                        {otherExperiences.map((exp, index) => (
                            <div key={index} className='bg-white rounded-3xl border border-gray-100 shadow-sm'>
                                <ExperienceCard 
                                    experience={exp} 
                                    index={index} 
                                    onClick={setSelectedExperience} 
                                    variant="overlay"
                                />
                            </div>
                        ))}
                    </div>
                </div>
                </motion.div>
                </AnimatePresence>

            </div>
            
            {/* Experience Details Modal */}
            <ExperienceModal 
                experience={selectedExperience} 
                onClose={() => setSelectedExperience(null)} 
            />
        </div>
    )
}

export default WhenToVisit
